import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import {
  SETTINGS_INSTRUCTIONS_MAX_LENGTH,
  useTenantSettings,
} from "@/hooks/use-settings";
import { cn } from "@/lib/utils";
import { SaveFab } from "./save-fab";
import { SettingsPageHeader } from "./settings-page-header";

const INPUT_CLASS =
  "mt-2 w-full rounded-[10px] border border-border bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]";
const READONLY_CLASS =
  "cursor-default bg-muted/30 text-muted-foreground focus:border-border focus:shadow-none";

export function TenantSettingsContent() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { instructions, loading, error, save } = useTenantSettings();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(instructions);
  }, [instructions]);

  const isDirty = useMemo(
    () => isAdmin && draft !== instructions,
    [draft, instructions, isAdmin],
  );

  const handleSave = async () => {
    if (!isAdmin || !isDirty || saving) return;

    setSaving(true);
    try {
      await save(draft);
      toast.success(t("settings.savedToast"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-10">
        <div className="mx-auto max-w-[880px] space-y-6">
          <SettingsPageHeader
            navKey="settings.nav.tenant"
            titleKey="settings.tenant.title"
            subtitleKey="settings.tenant.description"
          />

          {error && !loading && (
            <div className="rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-[13px] text-muted-foreground">
              <Spinner size="sm" />
              <span>{t("common.loading")}</span>
            </div>
          ) : (
            <section className="grid gap-2">
              <div className="flex items-center justify-end gap-3">
                <span
                  className={cn(
                    "text-[12px] text-muted-foreground tabular-nums",
                    draft.length >= SETTINGS_INSTRUCTIONS_MAX_LENGTH &&
                      "text-destructive",
                  )}
                >
                  {draft.length}/{SETTINGS_INSTRUCTIONS_MAX_LENGTH}
                </span>
              </div>
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("settings.tenant.placeholder")}
                rows={16}
                maxLength={SETTINGS_INSTRUCTIONS_MAX_LENGTH}
                readOnly={!isAdmin}
                className={cn(
                  INPUT_CLASS,
                  "min-h-[320px] resize-y text-[13px] leading-relaxed",
                  !isAdmin && READONLY_CLASS,
                )}
              />
            </section>
          )}
        </div>
      </div>

      <SaveFab
        visible={!loading && isDirty}
        loading={saving}
        onClick={handleSave}
      />
    </div>
  );
}
